import {
  Bell,
  CreditCard,
  DollarSign,
  RefreshCw,
  Settings,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { useEffect, useState } from "react";

import { Button } from "../ui/button";
import { api } from "@cvx/_generated/api";
import { useAction } from "convex/react";
import { useToast } from "../ui/use-toast";

// Global types for Connect.js
declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    StripeConnect?: any;
  }
}

export default function StripeConnectElements() {
  const [activeTab, setActiveTab] = useState("notifications");
  const [refreshing, setRefreshing] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [connectInstance, setConnectInstance] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [mountedComponents, setMountedComponents] = useState<Set<string>>(
    new Set(),
  );
  const [notificationCount, setNotificationCount] = useState<{
    total: number;
    actionRequired: number;
  }>({ total: 0, actionRequired: 0 });

  const createConnectAccountSession = useAction(
    api.stripe.createConnectAccountSession,
  );
  const { toast } = useToast();

  const cleanupComponent = (componentName: string) => {
    setMountedComponents((prev) => {
      const newSet = new Set(prev);
      newSet.delete(componentName);
      return newSet;
    });
  };

  const createComponent = (
    componentName: string,
    containerId: string,
    maxRetries = 3,
  ) => {
    if (!connectInstance) return;

    // Check if component is already mounted and container exists
    if (mountedComponents.has(componentName)) {
      const existingContainer = document.getElementById(containerId);
      if (existingContainer && existingContainer.children.length > 0) {
        return;
      } else {
        cleanupComponent(componentName);
      }
    }

    const attemptCreation = (attempt: number) => {
      const container = document.getElementById(containerId);
      if (container) {
        try {
          // Clear any existing content in the container
          container.innerHTML = "";

          const component = connectInstance.create(componentName);

          // Special handling for notification-banner
          if (componentName === "notification-banner") {
            // Track notification changes
            component.setOnNotificationsChange(
              (response: { total: number; actionRequired: number }) => {
                setNotificationCount(response);
              },
            );

            // Set collection options to show more notifications
            component.setCollectionOptions({
              fields: "eventually_due",
              futureRequirements: "include",
            });
          }

          container.appendChild(component);
          setMountedComponents((prev) => new Set(prev).add(componentName));
        } catch (error) {
          console.error(`Error creating ${componentName} component:`, error);

          // Retry if we haven't exceeded max attempts
          if (attempt < maxRetries) {
            setTimeout(() => attemptCreation(attempt + 1), 1000);
          }
        }
      } else {
        // Retry if we haven't exceeded max attempts
        if (attempt < maxRetries) {
          setTimeout(() => attemptCreation(attempt + 1), 500);
        }
      }
    };

    attemptCreation(1);
  };

  const createBalanceComponent = () => {
    if (!connectInstance) return;
    createComponent("balances", "balances-container", 5); // More retries for balance since it's always visible
  };

  const createTabComponent = (componentName: string, containerId: string) => {
    createComponent(componentName, containerId, 3);
  };

  // Clean up components when switching tabs
  useEffect(() => {
    if (!connectInstance) return;

    // Map tabs to their components
    const tabComponentMap = {
      notifications: {
        component: "notification-banner",
        container: "notification-banner-container",
      },
      account: {
        component: "account-management",
        container: "account-management-container",
      },
      payouts: { component: "payouts", container: "payouts-container" },
      payments: { component: "payments", container: "payments-container" },
    };

    // Clean up components from other tabs
    Object.entries(tabComponentMap).forEach(([tabName, config]) => {
      if (tabName !== activeTab) {
        cleanupComponent(config.component);
      }
    });

    const tabConfig =
      tabComponentMap[activeTab as keyof typeof tabComponentMap];
    if (tabConfig) {
      // Small delay to ensure the tab content is rendered
      setTimeout(() => {
        createTabComponent(tabConfig.component, tabConfig.container);
      }, 100);
    }
  }, [activeTab, connectInstance]);

  // Create balance component when connectInstance is available
  useEffect(() => {
    if (!connectInstance) return;

    // Small delay to ensure the balance container is rendered
    setTimeout(() => {
      createBalanceComponent();
    }, 200);
  }, [connectInstance]);

  // Clean up all components when component unmounts
  useEffect(() => {
    return () => {
      setMountedComponents(new Set());
    };
  }, []);

  useEffect(() => {
    let mounted = true;

    const initializeConnectComponents = async () => {
      try {
        // Load Connect.js script if not already loaded
        if (!window.StripeConnect) {
          const script = document.createElement("script");
          script.src = "https://connect-js.stripe.com/v1.0/connect.js";
          script.async = true;
          document.head.appendChild(script);

          await new Promise<void>((resolve, reject) => {
            script.onload = () => {
              resolve();
            };
            script.onerror = () => {
              console.error("Failed to load Connect.js script");
              reject(new Error("Failed to load Connect.js"));
            };
          });
        }

        // Get account session client secret
        const clientSecret = await createConnectAccountSession({});

        if (!mounted) return;

        // Initialize Connect instance
        const instance = window.StripeConnect.init({
          publishableKey: import.meta.env.VITE_STRIPE_PUBLIC_KEY,
          fetchClientSecret: async () => clientSecret,
        });

        setConnectInstance(instance);
        setLoading(false);
      } catch (error) {
        console.error("Error initializing Connect components:", error);
        if (mounted) {
          toast({
            title: "Error",
            description: "Failed to load Connect components. Please try again.",
            variant: "destructive",
          });
          setLoading(false);
        }
      }
    };

    initializeConnectComponents();

    return () => {
      mounted = false;
    };
  }, [createConnectAccountSession, toast]);

  const handleRefresh = async () => {
    setRefreshing(true);
    // Force refresh all embedded components
    window.location.reload();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-sm text-muted-foreground">
            Loading Stripe Connect components...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Balance Overview */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5" />
            Balance Overview
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div id="balances-container" className="min-h-[200px]">
            {/* Stripe Connect Balances component will be rendered here */}
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center justify-between">
        <div></div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center gap-2"
        >
          <RefreshCw
            className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`}
          />
          Refresh
        </Button>
      </div>

      <Tabs
        value={activeTab}
        onValueChange={(value) => {
          setActiveTab(value);
        }}
        className="w-full"
      >
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger
            value="notifications"
            className="flex items-center gap-2"
          >
            <Bell className="h-4 w-4" />
            Notifications
            {notificationCount.actionRequired > 0 && (
              <span className="bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center ml-1">
                {notificationCount.actionRequired}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="account" className="flex items-center gap-2">
            <Settings className="h-4 w-4" />
            Account
          </TabsTrigger>
          <TabsTrigger value="payouts" className="flex items-center gap-2">
            <DollarSign className="h-4 w-4" />
            Payouts
          </TabsTrigger>
          <TabsTrigger value="payments" className="flex items-center gap-2">
            <CreditCard className="h-4 w-4" />
            Payments
          </TabsTrigger>
        </TabsList>

        <TabsContent value="notifications" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="h-5 w-5" />
                Notifications
                {notificationCount.total > 0 && (
                  <span className="text-sm text-muted-foreground">
                    ({notificationCount.total} total,{" "}
                    {notificationCount.actionRequired} requiring action)
                  </span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div id="notification-banner-container" className="min-h-[100px]">
                {/* Stripe Connect NotificationBanner component will be rendered here */}
                {notificationCount.total === 0 &&
                  mountedComponents.has("notification-banner") && (
                    <div className="text-center py-8 text-muted-foreground">
                      <Bell className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p className="text-lg font-medium">
                        No notifications at this time
                      </p>
                      <p className="text-sm">
                        Your account is up to date with all requirements.
                      </p>
                    </div>
                  )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="account" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5" />
                Account Management
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div id="account-management-container" className="min-h-[400px]">
                {/* Stripe Connect AccountManagement component will be rendered here */}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="payouts" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5" />
                Payouts
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div id="payouts-container" className="min-h-[400px]">
                {/* Stripe Connect Payouts component will be rendered here */}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="payments" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="h-5 w-5" />
                Payments
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div id="payments-container" className="min-h-[400px]">
                {/* Stripe Connect Payments component will be rendered here */}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
